import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Square, Volume2, ChevronDown } from 'lucide-react';

const FEATURED = [
  {
    name: 'Hindi',
    audio: '/audio/female_hindi_audio.wav',
    script:
      'Namaste! Main Neurobloom ka AI voice agent hun. Aapne apni cart mein kuch items chhode hain. Kya aap abhi apna order complete karna chahenge? Main aapki madad kar sakti hun.',
  },
  {
    name: 'English',
    audio: '/audio/female_english_audio.wav',
    script:
      "Hello! This is your Neurobloom AI voice agent calling. We noticed you left a few items in your cart. Would you like to complete your order today? I'm here to help with any questions you have about the products — happy to assist!",
  },
];

const MORE_LANGUAGES = [
  {
    name: 'Tamil',
    audio: '/audio/female_tamil_audio.wav',
    script:
      'Vanakkam! Naan Neurobloom AI voice agent. Neengal ungal cart-il sila items vittuvitteeargal. Ippo order complete seiya virukireeargala? Ungalukku help seiya ready irukkeen.',
  },
  {
    name: 'Telugu',
    audio: '/audio/female_telugu_audio.wav',
    script:
      'Namaskaram! Nenu Neurobloom AI voice agent ni. Meeru mee cart lo konni items vididipoyaru. Ipudu order complete cheyyalani anukuntunnara? Nenu meeku help cheyyadaniki ready ga unnanu.',
  },
  {
    name: 'Kannada',
    audio: '/audio/female_kannada_audio.wav',
    script:
      'Namaskara! Naanu Neurobloom AI voice agent. Neevu nimmaa cart nalli kelavu items bittiddeeri. Ega order complete maadabekku anta iddira? Naanu nimage help maadadakke ready aagiddene.',
  },
  {
    name: 'Malayalam',
    audio: '/audio/female_malayalam_audio.wav',
    script:
      'Namaskaram! Ente Neurobloom AI voice agent aanu. Ningal cart-il chaila items vittu. Ippo order complete cheyyanam ennu vicharikunnundo? Njan ningale sahayam cheyyaan ready aanu.',
  },
  {
    name: 'Bengali',
    audio: '/audio/female_bengali_audio.wav',
    script:
      'Namaskar! Ami Neurobloom AI voice agent. Apni aapnar cart-e kichhu items rakhiye giyechen. Akhon apnar order complete korte chai? Ami apnaake help korte pari.',
  },
  {
    name: 'Marathi',
    audio: '/audio/female_marathi_audio.wav',
    script:
      'Namaskar! Mi Neurobloom AI voice agent ahe. Tumhi tumchya cart madhe kahi items sodle ahet. Ka tumhala ata order complete karayacha ahe? Mi tumhala madad karayala tayyar ahe.',
  },
  {
    name: 'Gujarati',
    audio: '/audio/female_gujarati_audio.wav',
    script:
      'Namaste! Hu Neurobloom ni AI voice agent chhu. Tamey tamari cart ma ketlik items mukine gaya chho. Shu tamey have order complete karva mango chho? Hu tamne help karva taiyaar chhu.',
  },
  {
    name: 'Punjabi',
    audio: '/audio/female_punjabi_audio.wav',
    script:
      'Sat Sri Akal! Main Neurobloom di AI voice agent haan. Tussi apni cart vich kuch items chhad ke gaye ho. Ki tussi hun apna order complete karna chahunde ho? Main tuhadi madad karan layi taiyaar haan.',
  },
];

const Waveform = ({ isPlaying }) => (
  <div className="flex items-end gap-0.5 h-8">
    {[3, 6, 4, 8, 5, 9, 4, 7, 3, 6, 8, 4, 7, 5, 9, 3, 6].map((h, i) => (
      <motion.div
        key={i}
        className="w-1 rounded-full bg-[#2563EB]"
        animate={
          isPlaying
            ? {
                height: [`${h * 2}px`, `${(h + 4) * 2}px`, `${h * 2}px`],
                opacity: [0.6, 1, 0.6],
              }
            : { height: '4px', opacity: 0.3 }
        }
        transition={{
          duration: 0.5 + i * 0.05,
          repeat: Infinity,
          repeatType: 'loop',
          ease: 'easeInOut',
          delay: i * 0.04,
        }}
      />
    ))}
  </div>
);

const AudioDemo = () => {
  const [selectedLang, setSelectedLang] = useState(FEATURED[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePlay = () => {
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(selectedLang.audio);
    audioRef.current = audio;

    audio.onplay = () => setIsPlaying(true);
    audio.onended = () => setIsPlaying(false);
    audio.onerror = () => setIsPlaying(false);
    audio.onpause = () => setIsPlaying(false);

    audio.play();
  };

  const handleLangChange = (lang) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setSelectedLang(lang);
  };

  const handleDropdownChange = (e) => {
    const lang = MORE_LANGUAGES.find((l) => l.name === e.target.value);
    if (lang) handleLangChange(lang);
  };

  const isMoreLang = MORE_LANGUAGES.some((l) => l.name === selectedLang.name);

  return (
    <section
      id="demo"
      data-testid="audio-demo-section"
      className="py-24 md:py-32 bg-[#FAFAF9]"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-[#2563EB] font-semibold text-sm uppercase tracking-wider">
            Hear the agent
          </span>
          <h2
            data-testid="demo-headline"
            className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C1917] tracking-tight"
          >
            Listen to our bot speak your language.
          </h2>
          <p className="mt-4 text-[#57534E] text-base md:text-lg max-w-2xl mx-auto">
            Select a language below. This is a real preview of how our agent
            sounds when it calls your customers.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white rounded-3xl border border-[#E7E5E4] shadow-sm overflow-hidden"
        >
          {/* Language select */}
          <div className="px-8 py-6 border-b border-[#E7E5E4]">
            <p className="text-sm font-semibold text-[#1C1917] mb-3">Select language</p>
            <div className="flex flex-wrap items-center gap-2">
              {/* Featured: Hindi + English */}
              {FEATURED.map((lang) => (
                <button
                  key={lang.name}
                  onClick={() => handleLangChange(lang)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                    selectedLang.name === lang.name
                      ? 'bg-[#2563EB] text-white border-[#2563EB]'
                      : 'bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#2563EB] hover:text-[#2563EB]'
                  }`}
                >
                  {lang.name}
                </button>
              ))}

              {/* Dropdown for remaining languages */}
              <div className="relative">
                <select
                  onChange={handleDropdownChange}
                  value={isMoreLang ? selectedLang.name : ''}
                  className={`appearance-none pl-4 pr-8 py-2 rounded-full text-sm font-medium border cursor-pointer transition-all outline-none ${
                    isMoreLang
                      ? 'bg-[#2563EB] text-white border-[#2563EB]'
                      : 'bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#2563EB] hover:text-[#2563EB]'
                  }`}
                >
                  <option value="" disabled>
                    More languages…
                  </option>
                  {MORE_LANGUAGES.map((lang) => (
                    <option key={lang.name} value={lang.name}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className={`pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${
                    isMoreLang ? 'text-white' : 'text-[#57534E]'
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Script preview + play */}
          <div className="px-8 py-8">
            <div className="bg-[#FAFAF9] rounded-2xl p-5 border border-[#E7E5E4] mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Volume2 className="w-4 h-4 text-[#2563EB]" />
                <span className="text-xs font-semibold text-[#2563EB] uppercase tracking-wide">
                  Sample script — {selectedLang.name}
                </span>
              </div>
              <p className="text-[#57534E] text-sm leading-relaxed">{selectedLang.script}</p>
            </div>

            <div className="flex items-center gap-6">
              <button
                onClick={handlePlay}
                className={`flex items-center gap-3 px-6 py-3 rounded-full font-semibold text-base transition-all ${
                  isPlaying
                    ? 'bg-[#EF4444] hover:bg-[#DC2626] text-white'
                    : 'bg-[#2563EB] hover:bg-[#1D4ED8] text-white'
                }`}
              >
                {isPlaying ? (
                  <>
                    <Square className="w-4 h-4 fill-white" />
                    Stop
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 fill-white" />
                    Play sample
                  </>
                )}
              </button>

              {isPlaying && <Waveform isPlaying={isPlaying} />}
            </div>

            <p className="mt-4 text-xs text-[#A8A29E]">
              This is one of our actual agent voice — the same one your customers will hear on a real call.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AudioDemo;
